<template>
  <div ref="rootEl" class="tw-relative tw-w-full">
    <button
      ref="triggerEl"
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="menuId"
      :aria-activedescendant="activeDescendant"
      :data-value="modelValue"
      :disabled="disabled"
      class="
        tw-flex
        tw-w-full
        tw-appearance-none
        tw-items-center
        tw-justify-between
        tw-gap-2
        tw-rounded-md
        tw-border
        tw-border-solid
        tw-border-sf-snow-darker
        tw-bg-white
        tw-px-3
        tw-py-2
        tw-text-left
        tw-text-sm
        tw-leading-none
        tw-text-sf-neutral-darker-1
        tw-transition-all
        hover:tw-border-sf-gray-light
        focus:tw-border-sf-primary-dark focus:tw-outline-none
        disabled:tw-cursor-not-allowed
        disabled:tw-border-sf-snow-darker
        disabled:tw-bg-sf-neutral-subtle-1
        disabled:tw-text-sf-gray-lighter
      "
      v-bind="$attrs"
      @click="toggle"
      @keydown="onKeydown"
    >
      <span class="tw-truncate">{{ selectedLabel }}</span>
      <i
        class="fa fa-chevron-down tw-shrink-0 tw-text-xs tw-text-sf-gray-light"
        aria-hidden="true"
      />
    </button>

    <!-- Rendered only while open so floating-ui measures a laid-out element. -->
    <div
      v-if="isOpen"
      :id="menuId"
      ref="menuEl"
      role="listbox"
      class="sf-dropdown2-menu sf-dropdown2-menu--open sf-select-menu"
      :style="menuStyle"
    >
      <div
        v-for="(option, index) in options"
        :id="optionId(index)"
        :key="`${option.label}_${option.value}`"
        role="option"
        :data-value="option.value"
        :aria-selected="option.value === modelValue"
        :aria-disabled="option.disabled || undefined"
        class="sf-dropdown2-item"
        :class="{
          'sf-select-option--active': index === activeIndex,
          'sf-select-option--disabled': option.disabled,
        }"
        @click="select(option)"
        @mousemove="activeIndex = index"
      >
        <span>{{ option.label }}</span>
        <span
          v-if="option.value === modelValue"
          class="sf-pad-l-1 fa fa-check tw-self-center"
          aria-hidden="true"
        />
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="V extends string | number | null | undefined, O extends {
  value: V
  label: string
  disabled?: boolean
}"
>
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from '@floating-ui/dom'

const props =
  defineProps<{
    options: O[]
    modelValue: V
    // Closes the whole control. An option can also be closed off on its own —
    // `disabled` on the option type above — so a caller can keep a value on
    // screen while ruling it out.
    disabled?: boolean
  }>()

const emit =
  defineEmits<{
    'update:modelValue': [value: V]
  }>()

defineOptions({ inheritAttrs: false })

// Keeps the menu clear of the viewport edge on every side.
const VIEWPORT_PADDING = 8
// Below this the menu is too short to scroll usefully, so it overflows the
// viewport edge instead and the flip has somewhere better to put it.
const MIN_MENU_HEIGHT = 120
// How long a run of keystrokes counts as one typeahead search.
const TYPEAHEAD_RESET_MS = 500
// Above the drawer and the report table it opens over.
const MENU_Z_INDEX = '99999'

const rootEl = ref<HTMLElement | null>(null)
const triggerEl = ref<HTMLButtonElement | null>(null)
const menuEl = ref<HTMLElement | null>(null)

const isOpen = ref(false)
const activeIndex = ref(-1)

const menuId = `sf-select-menu-${useId()}`
const optionId = (index: number) => `${menuId}-option-${index}`

const selectedLabel = computed(
  () => props.options.find((option) => option.value === props.modelValue)?.label
)

const activeDescendant = computed(() =>
  isOpen.value && activeIndex.value >= 0
    ? optionId(activeIndex.value)
    : undefined
)

// The menu is positioned `fixed` so the drawer it opens inside cannot clip it
// at its scroll edge. That has to be set inline: ui-kit's `.sf-dropdown2-menu`
// declares `position: absolute` and `min-width: 100%` at the same specificity
// as anything this component could add, and a fixed menu resolves a percentage
// min-width against the viewport rather than the trigger.
const top = ref(0)
const left = ref(0)
const positioned = ref(false)

const menuStyle = computed(() => ({
  position: 'fixed' as const,
  top: `${top.value}px`,
  left: `${left.value}px`,
  margin: '0',
  overflowY: 'auto' as const,
  zIndex: MENU_Z_INDEX,
  // Until the first measurement the menu sits at the viewport corner, which
  // would show as a flash somewhere it never belonged.
  visibility: positioned.value ? ('visible' as const) : ('hidden' as const),
}))

async function updatePosition(): Promise<void> {
  if (!triggerEl.value || !menuEl.value) return

  // Measure the menu at its natural height. `size` below caps it to the room
  // below the trigger, and a cap left over from the previous run is exactly the
  // overflow `flip` reads to decide whether to open upwards instead: leave it
  // on and the menu always stays put, however little room is under it.
  menuEl.value.style.maxHeight = ''

  const { x, y } = await computePosition(triggerEl.value, menuEl.value, {
    strategy: 'fixed',
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ padding: VIEWPORT_PADDING }),
      shift({ padding: VIEWPORT_PADDING }),
      size({
        padding: VIEWPORT_PADDING,
        apply({ availableHeight, rects, elements }) {
          // Written straight to the element rather than through the bound
          // style, so the reset above is what the next measurement sees.
          elements.floating.style.maxHeight = `${Math.max(
            availableHeight,
            MIN_MENU_HEIGHT
          )}px`
          // A floor, not a width: the period labels are long and the fields sit
          // in a two-column grid, so the menu has to grow past its trigger.
          elements.floating.style.minWidth = `${rects.reference.width}px`
        },
      }),
    ],
  })

  top.value = y
  left.value = x
  positioned.value = true
}

let stopAutoUpdate: (() => void) | null = null

function open(index: number): void {
  if (props.disabled || isOpen.value) return

  isOpen.value = true
  activeIndex.value = index

  nextTick(() => {
    if (!triggerEl.value || !menuEl.value) return

    stopAutoUpdate = autoUpdate(triggerEl.value, menuEl.value, updatePosition)
    scrollActiveIntoView()
  })
}

function close(): void {
  isOpen.value = false
  activeIndex.value = -1
  positioned.value = false
  stopAutoUpdate?.()
  stopAutoUpdate = null
}

function toggle(): void {
  if (isOpen.value) close()
  else open(selectedIndex())
}

function select(option: O): void {
  if (option.disabled) return

  emit('update:modelValue', option.value)
  close()
  triggerEl.value?.focus()
}

const selectedIndex = (): number => {
  const index = props.options.findIndex(
    (option) => option.value === props.modelValue
  )
  return index === -1 ? firstEnabled(0, 1) : index
}

// Walks from `from` in `step`s until it lands on an option the caller may pick.
// Returns -1 when the walk runs off the list or finds nothing but ruled-out
// options, so no caller can park the highlight somewhere unusable.
function firstEnabled(from: number, step: number): number {
  const { options } = props

  for (let i = from; i >= 0 && i < options.length; i += step) {
    if (!options[i].disabled) return i
  }

  return -1
}

function highlight(index: number): void {
  if (index === -1) return

  activeIndex.value = index
  scrollActiveIntoView()
}

function move(step: number): void {
  highlight(firstEnabled(activeIndex.value + step, step))
}

function scrollActiveIntoView(): void {
  nextTick(() => {
    // Queried rather than held as a ref array, which Vue does not guarantee to
    // come back in the order the options were rendered in.
    const options =
      menuEl.value?.querySelectorAll<HTMLElement>('[role="option"]')
    options?.[activeIndex.value]?.scrollIntoView({ block: 'nearest' })
  })
}

// Matches what the browser did for the native <select> this replaced: typing
// jumps to the next option starting with what was typed.
let typeahead = ''
let typeaheadTimer: ReturnType<typeof setTimeout> | null = null

function jumpTo(character: string): void {
  if (typeaheadTimer) clearTimeout(typeaheadTimer)
  typeaheadTimer = setTimeout(() => (typeahead = ''), TYPEAHEAD_RESET_MS)

  typeahead += character.toLowerCase()

  const from = Math.max(isOpen.value ? activeIndex.value : selectedIndex(), 0)
  const ordered = [
    ...props.options.slice(from + 1),
    ...props.options.slice(0, from + 1),
  ]
  const match = ordered.find(
    (option) =>
      !option.disabled && option.label.toLowerCase().startsWith(typeahead)
  )

  if (!match) return

  const index = props.options.indexOf(match)

  if (isOpen.value) {
    activeIndex.value = index
    scrollActiveIntoView()
  } else {
    emit('update:modelValue', match.value)
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (props.disabled) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (isOpen.value) move(1)
      else open(selectedIndex())
      return
    case 'ArrowUp':
      event.preventDefault()
      if (isOpen.value) move(-1)
      else open(selectedIndex())
      return
    case 'Home':
      if (!isOpen.value) return
      event.preventDefault()
      highlight(firstEnabled(0, 1))
      return
    case 'End':
      if (!isOpen.value) return
      event.preventDefault()
      highlight(firstEnabled(props.options.length - 1, -1))
      return
    case 'Enter':
    case ' ': {
      event.preventDefault()

      if (!isOpen.value) {
        open(selectedIndex())
        return
      }

      // No highlight to act on when every option is ruled out, or when the list
      // emptied under the open menu.
      const highlighted = props.options[activeIndex.value]

      if (highlighted) select(highlighted)

      return
    }
    case 'Escape':
      if (!isOpen.value) return
      event.preventDefault()
      close()
      return
    case 'Tab':
      close()
      return
    default:
      if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        jumpTo(event.key)
      }
  }
}

onClickOutside(rootEl, () => close())

// An option list that changes under an open menu (another field narrowed this
// one) can leave the highlight past the end of the list.
watch(
  () => props.options.length,
  (length) => {
    if (activeIndex.value >= length) activeIndex.value = length - 1
  }
)

onBeforeUnmount(() => {
  stopAutoUpdate?.()
  if (typeaheadTimer) clearTimeout(typeaheadTimer)
})
</script>

<style lang="scss" scoped>
// ui-kit gives every item the same hover background. A ruled-out option must
// not answer the pointer that way, and the keyboard highlight has to read the
// same as hover does for the mouse.
.sf-select-option--active {
  background-color: theme('colors.sf-snow.dark');
}

.sf-select-option--disabled,
.sf-select-option--disabled:hover {
  background-color: transparent;
  color: theme('colors.sf-gray.lighter');
  cursor: not-allowed;
}

// The menu scrolls, so the last item's border would otherwise sit above the
// rounded bottom edge.
.sf-select-menu .sf-dropdown2-item:last-child {
  border-bottom: 0;
}
</style>
